/**
 * Sharing a document (spec §11.1, §1.2).
 *
 * §11.1: "**WhatsApp share** uses the **Web Share API** with the signed public
 * PDF URL, falling back to a `wa.me` deep link. (Under Capacitor,
 * `@capacitor/share` supplies the native share sheet — same call site.)"
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS THE SEAM, AND IT EXISTS SO PHASE 8 IS ONE FILE.
 *
 * The screen never touches `navigator.share`. It calls {@link shareDocument},
 * which asks {@link getShareAdapter} for whatever adapter is installed. Today
 * that is {@link webShareAdapter}. When §1.2's Capacitor shell arrives, Phase 8
 * writes a `capacitorShareAdapter` (a `Share.share({ files })` call and a
 * `Filesystem.writeFile` to give it a path) and calls {@link setShareAdapter}
 * once at start-up. `AfterSaveScreen.tsx` does not change, and neither does any
 * test of it.
 *
 * The adapter also reports whether it can carry a FILE. That is not a detail:
 * on Android Chrome, sharing the PDF itself lands a real document in the
 * WhatsApp chat, whereas sharing a link lands a link the customer's accounts
 * team has to open. The first is what the owner does today with a photo of the
 * bill; the second is a worse version of it.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHY A `wa.me` LINK IS THE FALLBACK AND NOT THE DEFAULT. A deep link can carry
 * TEXT only — no attachment — so it opens WhatsApp with a message the owner
 * still has to attach the bill to by hand. It is the right answer on a desktop
 * browser with no share sheet, and the wrong answer everywhere else.
 *
 * ON THE "SIGNED PUBLIC PDF URL". §7 lists `GET /invoices/:id/whatsapp-link`
 * returning a `wa.me` link plus a short signed PDF URL, and it is NOT built yet
 * — the deployed API has no such route. Until it exists there is no URL that a
 * customer (who has no session) could open, so this module shares the FILE it
 * already fetched with the owner's bearer token. When that endpoint lands, the
 * change is to fill {@link SharePayload.url} at the call site; nothing here
 * needs to move, because a payload with both a file and a URL already prefers
 * the file and falls back to the URL.
 */

/* -------------------------------------------------------------------------- */
/* The payload                                                                 */
/* -------------------------------------------------------------------------- */

export interface ShareFile {
  blob: Blob;
  /** The name the recipient sees in their chat. Include the document number. */
  filename: string;
}

export interface SharePayload {
  /** Short title for the OS share sheet, e.g. "Bill 26-27/67". */
  title: string;
  /** The message body. Also the `wa.me` text when the sheet is unavailable. */
  text: string;
  /**
   * A publicly openable link, when one exists (§7's signed short URL). Optional
   * because it does not exist yet — see the module comment.
   */
  url?: string;
  /** The document itself. Preferred over `url` when the platform can carry it. */
  file?: ShareFile;
  /**
   * The customer's phone, for the `wa.me` fallback. Any Indian format; it is
   * normalised by {@link toWhatsAppNumber}. Absent opens WhatsApp with no
   * recipient chosen, which still works — he picks the chat.
   */
  phone?: string | null;
}

/**
 * What happened, for the screen to report honestly.
 *
 * `dismissed` is NOT an error and must never surface as one: the owner opened
 * the sheet, looked at it and backed out. A red "sharing failed" toast for that
 * is the app calling him wrong for changing his mind.
 */
export type ShareOutcome =
  | { status: "shared"; via: "sheet" }
  | { status: "dismissed" }
  | { status: "fallback"; via: "wa.me" }
  | { status: "failed"; reason: "no-target" };

export interface ShareAdapter {
  /** Identifies the installed adapter in logs and in tests. */
  readonly id: string;
  /** Can this platform put an actual FILE into the share sheet right now? */
  canShareFile: (file: ShareFile) => boolean;
  share: (payload: SharePayload) => Promise<ShareOutcome>;
}

/* -------------------------------------------------------------------------- */
/* wa.me                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * WhatsApp's own deep-link host. NOT a SteelBill hostname, so §13's "no
 * hostname string literals in source" does not reach it — that rule is about
 * our servers, whose address is deployment configuration. `wa.me` is a fixed
 * part of a third-party protocol, in the same category as `mailto:`, and
 * putting it in an env var would imply it could differ per environment.
 */
const WHATSAPP_DEEP_LINK_ORIGIN = "https://wa.me";

/** India. Every customer of this business is Indian (§8). */
const INDIA_DIALLING_CODE = "91";

/**
 * A phone as `wa.me` wants it: digits only, with the country code, no `+`.
 *
 * Handles what is actually stored on a customer row — `9822998896`,
 * `09822998896`, `+91 98229 98896`, `91-9822998896`. Returns `null` rather than
 * guessing when the result is not a plausible number, because a `wa.me` link to
 * a wrong number opens a chat with a stranger and the owner may not notice
 * before typing.
 */
export function toWhatsAppNumber(phone: string | null | undefined): string | null {
  if (phone === null || phone === undefined) return null;

  let digits = phone.replace(/\D/g, "");
  if (digits === "") return null;

  // A leading trunk zero is a domestic-dialling artefact, never part of the number.
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length === 10) digits = `${INDIA_DIALLING_CODE}${digits}`;

  // 12 = 91 + ten digits. Longer or shorter is not a number we can address.
  if (digits.length !== 12 || !digits.startsWith(INDIA_DIALLING_CODE)) return null;
  return digits;
}

/**
 * `https://wa.me/919822998896?text=…`, or the chooser form when there is no
 * usable number.
 */
export function whatsAppDeepLink(text: string, phone?: string | null): string {
  const number = toWhatsAppNumber(phone);
  const query = `?text=${encodeURIComponent(text)}`;
  return number === null
    ? `${WHATSAPP_DEEP_LINK_ORIGIN}/${query}`
    : `${WHATSAPP_DEEP_LINK_ORIGIN}/${number}${query}`;
}

/* -------------------------------------------------------------------------- */
/* The web adapter                                                             */
/* -------------------------------------------------------------------------- */

/**
 * `navigator.share` and `navigator.canShare`, without asserting they exist.
 *
 * Not `extends Navigator`: the DOM lib declares both members as REQUIRED, which
 * is exactly the claim this file exists to avoid making — desktop Chrome has
 * neither. `Omit` drops the lib's version and re-declares them optional.
 */
interface ShareCapableNavigator extends Omit<Navigator, "share" | "canShare"> {
  share?: (data: ShareData) => Promise<void>;
  canShare?: (data: ShareData) => boolean;
}

function nav(): ShareCapableNavigator | null {
  return typeof navigator === "undefined" ? null : (navigator as ShareCapableNavigator);
}

/**
 * `AbortError` is what every browser throws when the user closes the sheet.
 * Distinguishing it from a real failure is the difference between "you changed
 * your mind" and "something is broken".
 */
function isDismissal(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

/** Opens a URL in a new tab, or navigates if the popup was blocked. */
function openExternal(url: string): void {
  if (typeof window === "undefined") return;
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (opened === null) window.location.assign(url);
}

export const webShareAdapter: ShareAdapter = {
  id: "web-share",

  canShareFile(file) {
    const navigatorRef = nav();
    if (navigatorRef?.share === undefined) return false;
    // `canShare` with files is Web Share Level 2. Desktop Chrome has `share`
    // but refuses files; asking first is the only way to know, and calling
    // `share({files})` blind throws a `NotAllowedError` the owner would see as
    // a broken button.
    if (navigatorRef.canShare === undefined) return false;
    if (typeof File === "undefined") return false;
    try {
      return navigatorRef.canShare({
        files: [new File([file.blob], file.filename, { type: "application/pdf" })],
      });
    } catch {
      return false;
    }
  },

  async share(payload) {
    const navigatorRef = nav();

    if (navigatorRef?.share !== undefined) {
      // The FILE first — see the module comment. A bill in the chat beats a
      // link to a bill.
      if (payload.file !== undefined && webShareAdapter.canShareFile(payload.file)) {
        try {
          await navigatorRef.share({
            title: payload.title,
            text: payload.text,
            files: [
              new File([payload.file.blob], payload.file.filename, { type: "application/pdf" }),
            ],
          });
          return { status: "shared", via: "sheet" };
        } catch (error) {
          if (isDismissal(error)) return { status: "dismissed" };
          // Fall through to the link paths: a sheet that refused the file may
          // still take text, and the owner still wants to send the bill.
        }
      }

      if (payload.url !== undefined) {
        try {
          await navigatorRef.share({ title: payload.title, text: payload.text, url: payload.url });
          return { status: "shared", via: "sheet" };
        } catch (error) {
          if (isDismissal(error)) return { status: "dismissed" };
        }
      }
    }

    // No sheet, or the sheet refused everything: §11.1's `wa.me` fallback.
    const text = payload.url === undefined ? payload.text : `${payload.text}\n${payload.url}`;
    openExternal(whatsAppDeepLink(text, payload.phone));
    return { status: "fallback", via: "wa.me" };
  },
};

/* -------------------------------------------------------------------------- */
/* The installed adapter                                                       */
/* -------------------------------------------------------------------------- */

let adapter: ShareAdapter = webShareAdapter;

/**
 * Install a different adapter. Phase 8 calls this once, from the Capacitor
 * bootstrap, before the first render. Tests call it to assert what a screen
 * asked for without opening anything.
 */
export function setShareAdapter(next: ShareAdapter): void {
  adapter = next;
}

/** Restore the browser adapter. For test teardown. */
export function resetShareAdapter(): void {
  adapter = webShareAdapter;
}

export function getShareAdapter(): ShareAdapter {
  return adapter;
}

/** The one call site a screen uses. */
export function shareDocument(payload: SharePayload): Promise<ShareOutcome> {
  return adapter.share(payload);
}
